import { FullDocument, SolrSearchResultDocItem } from "~/features/Search/Results/types";

import {
    SEARCH_ITEMS,
    CategoryItem,
    DetailsResponseItem,
    DisclosureItem,
    NormalizedCategory,
    NormalizedCompany,
    NormalizedDisclosure,
    NormalizedTopic
} from "~/types/search.types";
import {
    GoodPracticeTopicData,
    MarketIntelligenceResData,
    NormalizedDisclosureRIG,
    NormalizedTopicRIG,
    NormalizedMarketIntelligenceData
} from "~/types/docs.types";

import { Language } from "~/providers/LanguageProvider";
import { ResCompanyData } from "~/types/filters.types";

export const normalizeDisclosureMetaData = (data: DisclosureItem[]) => {
    return data.map(
        (item, id) =>
            ({
                id,
                exploreId: item.id,
                type: SEARCH_ITEMS.DISCLOSURE,
                label: {
                    [Language.EN]: item.name,
                    [Language.FR]: item?.frName || item?.name || ""
                },
                isSelected: false,
                count: 0,
                selectedCategories: []
            } as NormalizedDisclosure)
    );
};

export const normalizeCategoryMetaData = (data: DetailsResponseItem[]) => {
    return data.reduce((list: NormalizedCategory[], disclosure) => {
        const categories = disclosure.CategorySections?.map((category: CategoryItem, idx) => ({
            id: idx,
            exploreId: category?.CategoryID || -1,
            disclosureId: category?.DocumentTypeID || -1,
            type: SEARCH_ITEMS.CATEGORY,
            label: {
                [Language.EN]: category?.CategoryName || "",
                [Language.FR]: category?.CategoryNameFr || category?.CategoryName || ""
            },
            isSelected: false,
            isFetched: false,
            count: category?.CategoryChild?.length || 0,
            selectedTopicIds: []
        }));

        return [...list, ...categories];
    }, []);
};

export const normalizeTopicMetaData = (data: DetailsResponseItem[]) => {
    return data.reduce((list: NormalizedTopic[], disclosure) => {
        disclosure.CategorySections.forEach((category: CategoryItem) => {
            const topics = category.CategoryChild.map((topic, id) => ({
                id,
                exploreId: topic.MetadataID || -1,
                categoryId: topic.CategoryID || -1,
                disclosureId: topic.DocumentTypeID || -1,
                type: SEARCH_ITEMS.TOPIC,
                label: {
                    [Language.EN]: topic.MetadataName || "",
                    [Language.FR]: topic.MetadataNameFr || topic.MetadataName || ""
                },
                isSelected: false,
                count: -1
            }));

            list = [...list, ...topics];
        });

        return list;
    }, []);
};

export const normalizeCompanyData = (data: ResCompanyData) => {
    return {
        id: data.CompanyId,
        industryId: data.IndustryID,
        name: data.Name,
        ticker: "",
        disclosureIds: [5, 6]
    } as NormalizedCompany;
};

export const normalizeRIGDisclosureData = (data: MarketIntelligenceResData) => {
    return {
        id: data.SourceDocumentID,
        disclosure: data.CategoryTags
    } as NormalizedDisclosureRIG;
};

export const normalizeRIGTopicData = (data: GoodPracticeTopicData, disclosureId: number) => {
    return {
        id: data.GoodPracticeId,
        disclosureId: disclosureId,
        topicId: data.MetaDataId,
        isBookmark: data.IsBookMark,
    } as NormalizedTopicRIG;
};

export const normalizeMarketIntelligenceData = (
    data: MarketIntelligenceResData
): NormalizedMarketIntelligenceData => {
    return {
        sourceDocumentID: data.SourceDocumentID,
        categoryTitle: data.CategoryTitle,
        categoryDescription: data.CategoryDescription,
        categoryTags: data.CategoryTags,
        thumbnailImage: data.ThumbnailImage
    };
};

export const normalizeFullDocumentItem = (doc: SolrSearchResultDocItem) => {
    const {
        ThumbnailPath: thumbnailImg,
        SourceDocumentID: documentID,
        SourceDocumentFilePath: documentFilePath,
        SourceDocumentName: documentName,
        DocumentTypeUs,
        DocumentTypeFr,
        CompanyName: company,
        Content: content = "",
        Year: year,
        Award
    } = doc;
    const thumbnail = `/files/thumbnail/${thumbnailImg}`;
    const award = Award ? `/files/PDF/${Award.FilePath}` : null;
    const documentType = {
        en: DocumentTypeUs,
        fr: DocumentTypeFr
    };

    return {
        documentID,
        documentFilePath,
        documentName,
        documentType,
        company,
        content,
        thumbnail,
        year,
        award
    } as FullDocument;
};
